import { ref, computed } from 'vue';
import { format, differenceInDays } from 'date-fns';
import { useComponentI18n } from '@rdrive/i18n';
import { getIconClass } from '@rdrive/helper-icon-class';
import messages from './messages/index.js';

// The table's initial sort: the latest status change first, as the grid always opened
const DEFAULT_SORT = [{ key: 'StatusCreatedDate', order: 'desc' }];

export default (props) => {
    // LOCAL composer with the warn flags off — see @rdrive/i18n for why the flags are
    // assigned rather than passed. (It read the app's GLOBAL composer before the move.)
    // The search label and the phone table's texts are DuckDbDataTable's own strings.
    const { t } = useComponentI18n(messages);

    // The paging, the sort, the debounced search and every reload are DuckDbDataTable's
    // (@rdrive/plugin-data-table, IA_ABSORPTION_PLAN 13.3b): it owns the page, page size and
    // sort (v-model'd on its tables, so the footer cannot drift from the rows), applies only
    // its latest load, and reloads from page 1 on a filter tick or dataLoaded turning true.
    const expanded = ref([]);
    const selectedSubItems = ref(null)
    const showSubItemsDialog = ref(false)

    const showSummaryReportDialog = ref(false)

    // The page and the search term of the rows ON SCREEN — both from the table's `itemsLoaded`,
    // which fires only for the load it applied. Expanded rows collapse when a different page's
    // rows arrive; the report filters by the term those rows were filtered by.
    let loadedPage = 1;
    let loadedSearch = '';
    const onItemsLoaded = ({ page, search }) => {
        if (page !== loadedPage) {
            expanded.value = [];
        }
        loadedPage = page;
        loadedSearch = search;
    };

    // The report must see the DEBOUNCED term and this grid's view, so it
    // matches exactly what the grid is showing (R5)
    const handleGenerateReport = async (options) => {
        if (!props.generateReport) return;
        await props.generateReport({
            ...options,
            itemsView: props.itemsView,
            search: loadedSearch
        });
        // The dialog closes on success and on failure, as it did before
        showSummaryReportDialog.value = false;
    };

    // Function to check if an item has multiple sub-items default is 1
    const hasSubItems = (item, minCount = 1) => {
        return item.subItems.length > minCount;
    };

    const hasAssignees = (item) => {
        return item.subItems.some(subItem => subItem.assigneeUserIds && subItem.assigneeUserIds.length > 0);
    };

    const getUniqueUsers = (subItems, count) => {
        // Get unique user IDs from all subItems
        const allUserIds = subItems
            .flatMap(item => item.assigneeUserIds || [])
            .filter(Boolean);
        const uniqueUserIds = [...new Set(allUserIds)];

        // Convert IDs to user objects using the users lookup
        const list = uniqueUserIds
            .filter(id => props.users && props.users[id])
            .map(id => ({
                id: id,
                displayName: props.users[id]?.displayName || '',
                avatarUrl: props.users[id]?.avatarUrl || ''
            }));

        // Sort by name, then put current user first
        const userId = props.currentUserId?.toString();
        list.sort((a, b) => {
            // Current user always first
            if (a.id === userId) return -1;
            if (b.id === userId) return 1;
            // Then sort alphabetically by displayName
            return (a.displayName || '').localeCompare(b.displayName || '');
        });

        return list.slice(0, count);
    }

    // The table's headers, already translated here (the table shows a `title` as-is). The keys
    // are the sort keys the app's fetchPage maps onto columns.
    const headers = computed(() => [
        { title: t('id'), key: 'clientId', align: 'start', sortable: true },
        { title: t('item'), key: 'profileId', align: 'start', sortable: true },
        { title: t('assignedTo'), key: 'AssigneeTeamId', sortable: true },
        { title: "", key: "AssigneeUserIds", sortable: true },
        { title: t('location'), key: 'locationId', align: 'start', sortable: true },
        { title: t('createdBy'), key: 'createdByTeamId', align: 'start', sortable: true },
        { title: t('updatedDate'), key: 'StatusCreatedDate', align: 'start', sortable: true },
        { title: t('status'), key: 'StatusId', align: 'start', sortable: true },
    ]);

    const subItemHeaders = [
        { title: t('subItem'), key: 'itemTemplateId', align: 'start', sortable: true },
        { title: t('assignedTo'), key: 'assigneeTeamId', align: 'start', sortable: true },
        { title: '', key: 'assigneeUserIds', align: 'start', sortable: false },
        { title: t('updatedDate'), key: 'updatedDate', align: 'start', sortable: true },
        { title: t('status'), key: 'statusId', align: 'start', sortable: true }
    ];

    const openUrl = (url) => props.openUrl(url);

    const toggleExpand = (item) => {
        const index = expanded.value.indexOf(item.id);
        if (index === -1) {
            expanded.value.push(item.id);
        } else {
            expanded.value.splice(index, 1);
        }
    };

    const isExpanded = (item) => {
        return expanded.value.includes(item.id);
    };

    const expandIcon = computed(() => (item) => {
        return isExpanded(item) ? 'mdi-chevron-down' : 'mdi-chevron-right';
    });

    const getRandomColor = (id) => {
        const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
        const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
        return colors[index];
    };

    const getInitials = (name) => {
        if (!name) return '';
        const words = name.split(' ');
        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
        } else if (words[0]) {
            return words[0].substring(0, 2).toUpperCase();
        }
        return '';
    };

    const getAvatarUrl = (avatarUrl) => {
        if (!avatarUrl) {
            return '';
        }
        // If the URL already includes a protocol, return as-is
        if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
            return avatarUrl;
        } else {
            // Otherwise, prepend https://
            return 'https://' + avatarUrl;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return format(date, 'yyyy-MM-dd');
    };

    const getDaysSince = (dateString) => {
        if (!dateString) return 0;
        try {
            const statusDate = new Date(dateString);
            if (isNaN(statusDate.getTime())) return 0;
            const today = new Date();
            return differenceInDays(today, statusDate);
        } catch {
            return 0;
        }
    };

    const getDaysSinceText = (dateString) => {
        const daysSince = getDaysSince(dateString);
        if (daysSince === 0) return t('today');
        if (daysSince === 1) return t('dayAgo', { daysSince });
        if (daysSince > 1) return t('daysAgo', { daysSince });
        return '';
    };

    // openUrl is the local wrapper, which shadows the prop of the same name
    // exactly as it did under <script setup> (setup state wins over props).
    return {
        t,
        defaultSort: DEFAULT_SORT,
        selectedSubItems,
        showSubItemsDialog,
        showSummaryReportDialog,
        onItemsLoaded,
        handleGenerateReport,
        hasSubItems,
        hasAssignees,
        getUniqueUsers,
        headers,
        subItemHeaders,
        openUrl,
        toggleExpand,
        isExpanded,
        expandIcon,
        getRandomColor,
        getInitials,
        getAvatarUrl,
        formatDate,
        getDaysSinceText,
        getIconClass
    };
};
